import { Activity, CheckCircle, ChevronDown, Copy, Edit, ExternalLink, Plus } from 'lucide-react'
import { useState } from 'react'
import { FaFacebook } from 'react-icons/fa'
import type { NodeExecution } from 'sprites-types/api/v2/workflows'
import { getToolResultSchema } from 'sprites-types/workflow/tools/common'
import {
  facebookAdsPixelManagement,
  type FacebookAdsGetPixelStatsResult,
  type FacebookAdsPixelManagementResult,
} from 'sprites-types/workflow/tools/facebookAdsPixelManagement'
import { ContextMenuWrapper, type ContextAction } from '../shared/ContextMenuWrapper'
import {
  createWorkflowToolComponent,
  type WorkflowToolComponentProps,
} from '../shared/WorkflowToolComponent'
import { SchemaValidationError } from '../shared/safeParseToolResult'

function formatNumber(value: number): string {
  return value.toLocaleString()
}

function formatTimestamp(timestamp: number | null | undefined): string {
  if (!timestamp) return 'N/A'
  return new Date(timestamp * 1000).toLocaleDateString()
}

function FacebookAdsPixelManagementDisplayComponent({ nodeExecution }: WorkflowToolComponentProps) {
  const schema = getToolResultSchema(facebookAdsPixelManagement, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)
  const [isExpanded, setIsExpanded] = useState(true)

  if (!parseResult.success) {
    return <SchemaValidationError error={parseResult.error} nodeExecution={nodeExecution} />
  }

  const result = parseResult.data

  // Extract metadata from inputs
  const inputs = nodeExecution.inputs.functionArgs as Record<string, unknown> | undefined
  const action = inputs?.action as Record<string, unknown> | undefined
  const actionName = action?.name as string | undefined
  const params = action?.params as Record<string, unknown> | undefined

  // Render based on action type - type narrowing based on actionName
  if (actionName === 'getPixelStats' && 'pixel_id' in result) {
    return (
      <PixelStatsDisplay result={result} isExpanded={isExpanded} setIsExpanded={setIsExpanded} />
    )
  }

  // Handle createPixel and updatePixel - type narrowing excludes stats result
  if ('id' in result) {
    return <PixelManagementDisplay result={result} actionName={actionName} params={params} />
  }

  return null
}

function PixelManagementDisplay({
  result,
  actionName,
  params,
}: {
  result: Exclude<FacebookAdsPixelManagementResult, FacebookAdsGetPixelStatsResult>
  actionName: string | undefined
  params: Record<string, unknown> | undefined
}) {
  const isCreate = actionName === 'createPixel' || ('created' in result && result.created)
  const name = 'name' in result ? result.name : undefined
  const adAccountId = params?.adAccountId as string | undefined

  const eventsManagerUrl = adAccountId
    ? `https://business.facebook.com/events_manager2/list/pixel/${result.id}/overview?act=${adAccountId}`
    : null

  const contextActions: ContextAction[] = [
    ...(eventsManagerUrl
      ? [
          {
            id: 'open-events-manager',
            label: 'Open in Events Manager',
            icon: <ExternalLink className="h-4 w-4" />,
            onClick: () => window.open(eventsManagerUrl, '_blank', 'noopener,noreferrer'),
          },
        ]
      : []),
    {
      id: 'copy-id',
      label: 'Copy Pixel ID',
      icon: <Copy className="h-4 w-4" />,
      onClick: () => navigator.clipboard.writeText(result.id),
    },
  ]

  return (
    <ContextMenuWrapper actions={contextActions}>
      <div className="rounded-3xl border border-gray-200/50 bg-white/70 text-black shadow-xl backdrop-blur-[8px]">
        <div className="flex items-start gap-4 px-4 py-3">
          <div className="mt-1 flex-shrink-0">
            <div className="rounded-full bg-blue-100 p-2">
              {isCreate ? (
                <Plus className="h-5 w-5 text-blue-600" />
              ) : (
                <Edit className="h-5 w-5 text-blue-600" />
              )}
            </div>
          </div>
          <div className="min-w-0 flex-1">
            <div className="mb-2 flex items-center gap-2">
              <FaFacebook className="h-4 w-4 text-[#0064e0]" />
              <div className="text-sm font-semibold text-gray-900">
                {isCreate ? 'Pixel Created' : 'Pixel Updated'}
              </div>
            </div>

            <div className="rounded-2xl border border-gray-200 bg-gray-50 px-3 py-2">
              {name && <div className="mb-1 text-sm font-medium text-gray-900">{name}</div>}
              <div className="space-y-1 text-xs">
                <div className="text-gray-500">Pixel ID: {result.id}</div>
                {adAccountId && <div className="text-gray-500">Ad Account: {adAccountId}</div>}
              </div>
            </div>

            {isCreate && (
              <div className="mt-2 flex items-center gap-1 text-xs text-gray-500">
                <CheckCircle className="h-3 w-3 text-green-600" />
                <span>Pixel is ready to track conversions</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </ContextMenuWrapper>
  )
}

function PixelStatsDisplay({
  result,
  isExpanded,
  setIsExpanded,
}: {
  result: FacebookAdsGetPixelStatsResult
  isExpanded: boolean
  setIsExpanded: (expanded: boolean) => void
}) {
  const { pixel_id, pixel_name, stats, total_events, event_types, time_range } = result

  // Context menu actions for stats display
  const statsContextActions: ContextAction[] = [
    {
      id: 'copy-pixel-id',
      label: 'Copy Pixel ID',
      icon: <Copy className="h-4 w-4" />,
      onClick: () => navigator.clipboard.writeText(pixel_id),
    },
  ]

  if (stats.length === 0 && total_events === 0) {
    return (
      <ContextMenuWrapper actions={statsContextActions}>
        <div className="rounded-3xl border border-gray-200/50 bg-white/70 text-black shadow-xl backdrop-blur-[8px]">
          <div className="flex items-center gap-4 px-4 py-3">
            <div className="flex-shrink-0">
              <Activity className="h-5 w-5 text-gray-400" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm text-gray-600">No pixel event data found</div>
              <div className="mt-1 text-xs text-gray-500">Pixel ID: {pixel_id}</div>
            </div>
          </div>
        </div>
      </ContextMenuWrapper>
    )
  }

  return (
    <div className="space-y-3">
      <ContextMenuWrapper actions={statsContextActions}>
        <div className="rounded-3xl border border-gray-200/50 bg-white/70 text-black shadow-xl backdrop-blur-[8px]">
          <div className="px-4 py-3">
            <div className="mb-3 flex items-center gap-2">
              <FaFacebook className="h-4 w-4 text-[#0064e0]" />
              <div className="text-sm font-semibold text-gray-900">Pixel Statistics</div>
            </div>

            <div className="mb-3 rounded-2xl border border-gray-200 bg-gray-50 p-3">
              <div className="mb-2 text-xs font-medium text-gray-500">Summary</div>
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div>
                  <div className="text-gray-500">Pixel Name</div>
                  <div className="font-semibold text-gray-900">{pixel_name || 'Unknown'}</div>
                  <div className="mt-0.5 font-mono text-[10px] text-gray-500">{pixel_id}</div>
                </div>
                <div>
                  <div className="text-gray-500">Total Events</div>
                  <div className="font-semibold text-gray-900">{formatNumber(total_events)}</div>
                </div>
                {time_range.start && (
                  <div>
                    <div className="text-gray-500">Start Date</div>
                    <div className="font-medium text-gray-900">
                      {formatTimestamp(time_range.start)}
                    </div>
                  </div>
                )}
                {time_range.end && (
                  <div>
                    <div className="text-gray-500">End Date</div>
                    <div className="font-medium text-gray-900">
                      {formatTimestamp(time_range.end)}
                    </div>
                  </div>
                )}
              </div>

              {event_types.length > 0 && (
                <div className="mt-3 border-t border-gray-200 pt-2">
                  <div className="mb-1 text-xs font-medium text-gray-500">Event Types</div>
                  <div className="space-y-1">
                    {event_types.slice(0, 3).map((eventType) => (
                      <div
                        key={eventType.event_name}
                        className="flex items-center justify-between text-xs"
                      >
                        <span className="text-gray-700">{eventType.event_name}</span>
                        <span className="font-medium text-gray-900">
                          {formatNumber(eventType.count)}
                        </span>
                      </div>
                    ))}
                    {event_types.length > 3 && (
                      <div className="text-xs text-gray-500">
                        +{event_types.length - 3} more event types
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {stats.length > 0 && (
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="flex w-full items-center justify-between text-xs text-gray-600 hover:text-gray-900"
              >
                <span>{isExpanded ? 'Hide' : 'Show'} detailed statistics</span>
                <ChevronDown
                  className={`h-3 w-3 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                />
              </button>
            )}
          </div>
        </div>
      </ContextMenuWrapper>

      {isExpanded && stats.length > 0 && (
        <div className="mx-8 space-y-2">
          {stats.map((stat, idx) => (
            <div
              key={idx}
              className="group rounded-xl border border-gray-200 bg-white px-4 py-2.5 shadow-sm transition-all hover:border-gray-300 hover:shadow"
            >
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-semibold text-gray-900">
                    {stat.aggregation === 'Unknown' ? 'Unknown Event' : stat.aggregation}
                  </div>
                  <div className="mt-0.5 flex items-center gap-2">
                    {stat.timestamp && (
                      <div className="text-[11px] text-gray-500">
                        {formatTimestamp(stat.timestamp)}
                      </div>
                    )}
                    {stat.value !== undefined &&
                      stat.value !== null &&
                      typeof stat.value === 'number' && (
                        <div className="inline-flex items-center gap-1 rounded-md border border-green-200/60 bg-green-50 px-2 py-0.5 text-[11px] font-semibold text-green-700">
                          💰 ${formatNumber(stat.value)}
                        </div>
                      )}
                  </div>
                </div>
                <div className="flex flex-shrink-0 items-baseline gap-1">
                  <div className="text-lg font-bold tabular-nums text-gray-900">
                    {formatNumber(stat.count)}
                  </div>
                  <div className="text-[10px] font-medium text-gray-500">events</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function downloadFacebookAdsPixelManagement(_nodeExecution: NodeExecution): Promise<void> {
  return Promise.resolve()
}

function facebookAdsPixelManagementToMarkdown(nodeExecution: NodeExecution): string {
  const schema = getToolResultSchema(facebookAdsPixelManagement, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return '## Error: Schema validation failed\n\nUnable to export results due to data structure mismatch.'
  }

  const result = parseResult.data

  // Extract metadata from inputs
  const inputs = nodeExecution.inputs.functionArgs as Record<string, unknown> | undefined
  const action = inputs?.action as Record<string, unknown> | undefined
  const actionName = action?.name as string | undefined
  const params = action?.params as Record<string, unknown> | undefined

  // Handle getPixelStats
  if (actionName === 'getPixelStats' && 'pixel_id' in result) {
    const { pixel_id, pixel_name, stats, total_events, event_types, time_range } = result

    if (stats.length === 0 && total_events === 0) {
      return `## Facebook Pixel Statistics\n\nNo pixel event data found.\n\n---\n`
    }

    let markdown = `## Facebook Pixel Statistics\n\n### Summary\n\n`
    if (pixel_name) markdown += `- **Pixel Name:** ${pixel_name}\n`
    markdown += `- **Pixel ID:** ${pixel_id}\n`
    markdown += `- **Total Events:** ${formatNumber(total_events)}\n`
    if (time_range.start) markdown += `- **Start Date:** ${formatTimestamp(time_range.start)}\n`
    if (time_range.end) markdown += `- **End Date:** ${formatTimestamp(time_range.end)}\n`
    markdown += '\n'

    if (event_types.length > 0) {
      markdown += `### Event Types\n\n`
      event_types.forEach((eventType) => {
        markdown += `- **${eventType.event_name}:** ${formatNumber(eventType.count)}\n`
      })
      markdown += '\n'
    }

    if (stats.length > 0) {
      markdown += `### Detailed Statistics\n\n`
      stats.forEach((stat) => {
        markdown += `#### ${stat.aggregation}\n`
        if (stat.timestamp) markdown += `*${formatTimestamp(stat.timestamp)}*\n`
        markdown += `- **Events:** ${formatNumber(stat.count)}\n`
        if (stat.value !== undefined && stat.value !== null && typeof stat.value === 'number') {
          markdown += `- **Value:** $${formatNumber(stat.value)}\n`
        }
        markdown += '\n'
      })
    }

    markdown += '---\n'
    return markdown
  }

  // Handle createPixel and updatePixel - type narrowed by checking for 'id'
  if (!('id' in result)) {
    return '## Error: Unexpected result format\n\nUnable to export pixel management results.'
  }

  const isCreate = actionName === 'createPixel' || ('created' in result && result.created)
  const name = 'name' in result ? result.name : undefined
  const adAccountId = params?.adAccountId as string | undefined

  let markdown = `## Facebook Pixel ${isCreate ? 'Created' : 'Updated'}\n\n`
  if (name) markdown += `**Name:** ${name}\n`
  markdown += `**Pixel ID:** ${result.id}\n`
  if (adAccountId) markdown += `**Ad Account:** ${adAccountId}\n`
  markdown += '\n---\n'

  return markdown
}

export const FacebookAdsPixelManagementDisplay = createWorkflowToolComponent(
  FacebookAdsPixelManagementDisplayComponent,
  downloadFacebookAdsPixelManagement,
  facebookAdsPixelManagementToMarkdown
)
